import { Button } from "@/components/ui/button";
import { usePublicTranslation } from "@/features/utils/i18n";
import { useQueryClient } from "@tanstack/react-query";
import { PhoneIcon } from "@phosphor-icons/react";
import { useEffect } from "react";
import { toast } from "sonner";
import { usePhoneHandoffStatus } from "../../hooks/use-public-login";

export interface PhoneHandoff {
  phone: string;
}

interface PhoneHandoffOverlayProps {
  publicId: string;
  handoff: PhoneHandoff;
  onClose: () => void;
}

/**
 * "Appel en cours": the chat steps aside while the conversation goes on by phone.
 *
 * The agent asks the person who picks up whether they are the one from this chat; a
 * yes signs the chat in (see the backend's `session/phone-handoff-challenge.ts`), and
 * this screen closes by itself — or once the call is over. "Revenir au chat" is always
 * there: an unanswered call only reads as over after a few minutes, and nobody should
 * be stuck behind this meanwhile.
 */
export function PhoneHandoffOverlay({
  publicId,
  handoff,
  onClose,
}: PhoneHandoffOverlayProps) {
  const { t } = usePublicTranslation();
  const queryClient = useQueryClient();
  const { data } = usePhoneHandoffStatus(publicId, true);
  const status = data?.status;

  useEffect(() => {
    if (status === "verified") {
      // The session now points at the verified customer: reload what depends on it.
      queryClient.invalidateQueries({ queryKey: ["public-customer", publicId] });
      queryClient.invalidateQueries({ queryKey: ["public-session", publicId] });
      toast.success(t("login.handoff_verified"));
      onClose();
    } else if (status === "ended") {
      onClose();
    }
  }, [status]);

  return (
    <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-6 bg-background px-6 text-center">
      <div className="relative flex h-16 w-16 items-center justify-center">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary/20" />
        <span className="relative flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground">
          <PhoneIcon className="h-6 w-6" />
        </span>
      </div>

      <div className="space-y-1">
        <h2 className="text-lg font-semibold">{t("login.handoff_title")}</h2>
        <p className="text-sm text-muted-foreground">
          {t("login.calling_description", { phone: handoff.phone })}
        </p>
      </div>

      <p className="max-w-xs text-sm text-muted-foreground">
        {status === "failed"
          ? t("login.handoff_failed")
          : t("login.handoff_hint")}
      </p>

      <Button variant="outline" onClick={onClose}>
        {t("login.handoff_back")}
      </Button>
    </div>
  );
}
